import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
} from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import Loader from "../../components/ui/Loader";
import { ArrowLeft, Briefcase, CheckCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const PAGE_SIZE = 10;

export default function ApprovedJobs() {
 const navigate = useNavigate();
 const [jobs, setJobs] = useState([]);
 const [filteredJobs, setFilteredJobs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [chartData, setChartData] = useState([]);
 const [filter, setFilter] = useState("7");
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);

 const filterOptions = [
 { label: "Last 7 Days", value: "7" },
 { label: "Last Month", value: "30" },
 { label: "Last 3 Months", value: "90" },
 { label: "Last Year", value: "365" },
 { label: "Overall", value: "all" },
 ];

 useEffect(() => {
 fetchJobs();
 }, []);

 useEffect(() => {
 applyFilter();
 }, [jobs, filter]);

 useEffect(() => {
 generateChartData();
 }, [filteredJobs, filter]);

 const fetchJobs = async () => {
 setLoading(true);
 try {
 const res = await api.get("/admin/jobs/approved");
 setJobs(res.data.jobs);
 } catch (err) {
 console.error("Failed to fetch approved jobs:", err);
 } finally {
 setLoading(false);
 }
 };

 const applyFilter = () => {
 if (!jobs.length) return setFilteredJobs([]);

 const now = new Date();
 let startDate;

 switch (filter) {
 case "7":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
 break;
 case "30":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
 break;
 case "90":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89);
 break;
 case "365":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 364);
 break;
 default:
 startDate = null;
 }

 const filtered = startDate
 ? jobs.filter(job => new Date(job.createdAt) >= startDate)
 : [...jobs];

 setFilteredJobs(filtered);
 setPage(1);
 setTotalPages(Math.ceil(filtered.length / PAGE_SIZE));
 };

 const generateChartData = () => {
 if (!filteredJobs.length) return setChartData([]);

 const now = new Date();
 let startDate;

 switch (filter) {
 case "7":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
 break;
 case "30":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
 break;
 case "90":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89);
 break;
 case "365":
 startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 364);
 break;
 default:
 startDate = null;
 }

 const dataMap = {};
 filteredJobs.forEach(job => {
 const createdAt = new Date(job.createdAt);
 const day = createdAt.toISOString().split("T")[0];
 dataMap[day] = (dataMap[day] || 0) + 1;
 });

 const tempData = [];
 if (filter !== "all") {
 const days = filter === "7" ? 7 : filter === "30" ? 30 : filter === "90" ? 90 : 365;
 for (let i = 0; i < days; i++) {
 const date = new Date(startDate);
 date.setDate(startDate.getDate() + i);
 const dayStr = date.toISOString().split("T")[0];
 tempData.push({ date: dayStr, count: dataMap[dayStr] || 0 });
 }
 } else {
 Object.keys(dataMap).sort().forEach(day => {
 tempData.push({ date: day, count: dataMap[day] });
 });
 }

 setChartData(tempData);
 };

 const handlePageChange = (newPage) => {
 if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
 };

 const paginatedJobs = filteredJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

 if (loading) return <Loader />;

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button 
 variant="outline" 
 onClick={() => navigate("/admin/dashboard")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Dashboard
 </Button>
 
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 shadow-lg">
 <CheckCircle className="w-6 h-6 text-white" />
 </div>
 <h1 className="text-3xl md:text-4xl font-bold">Approved Jobs</h1>
 </div>
 <p className="text-muted-foreground">Total Approved Jobs: {filteredJobs.length}</p>
 </div>

 {/* Chart Card */}
 <Card className="mb-8">
 <CardHeader className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
 <CardTitle>Job Approvals Over Time</CardTitle>
 <Select value={filter} onValueChange={setFilter}>
 <SelectTrigger className="w-48 mt-2 sm:mt-0">
 <SelectValue placeholder="Select filter" />
 </SelectTrigger>
 <SelectContent>
 {filterOptions.map(opt => (
 <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </CardHeader>
 <CardContent>
 {chartData.length ? (
 <div className="w-full h-80">
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={chartData}>
 <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
 <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
 <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
 <Tooltip 
 contentStyle={{
 backgroundColor: "hsl(var(--card))",
 border: "1px solid hsl(var(--border))",
 borderRadius: "8px"
 }}
 />
 <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} />
 </LineChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="text-center py-12">
 <p className="text-muted-foreground">No jobs to display in the selected period.</p>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Jobs List */}
 <Card>
 <CardHeader>
 <CardTitle>Approved Jobs List</CardTitle>
 </CardHeader>
 <CardContent>
 {paginatedJobs.length ? (
 <>
 <div className="space-y-4">
 {paginatedJobs.map(job => (
 <Card key={job.id} className="hover:shadow-md transition-shadow">
 <CardContent className="p-5">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
 <div className="flex-1 min-w-0">
 <h3 className="font-semibold text-lg mb-1 truncate">{job.title}</h3>
 <p className="text-sm text-muted-foreground mb-1">
 {job.recruiter?.companyName || "Unknown Company"}
 </p>
 <p className="text-xs text-muted-foreground">{job.recruiter?.email}</p>
 </div>
 <div className="text-sm text-muted-foreground">
 Approved: {new Date(job.createdAt).toLocaleDateString()}
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex justify-center items-center gap-4 mt-6">
 <Button 
 size="sm" 
 variant="outline"
 disabled={page === 1} 
 onClick={() => handlePageChange(page - 1)}
 >
 Previous
 </Button>
 <span className="text-sm font-medium">
 Page {page} of {totalPages}
 </span>
 <Button 
 size="sm" 
 variant="outline"
 disabled={page === totalPages} 
 onClick={() => handlePageChange(page + 1)}
 >
 Next
 </Button>
 </div>
 )}
 </>
 ) : (
 <div className="text-center py-12">
 <Briefcase className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
 <p className="text-muted-foreground">No approved jobs yet.</p>
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </div>
 );
}
